import { useEffect, useRef } from "react";
import {
  MIN_RECT_SIZE,
  rebuildHitAreaAnimationEntries,
  toNumber,
  wouldHitAreaScaleFallBelowMinSize,
} from "../../components/RightPanel/HitAreaModal/utils";
import { scaleHitAreasForImageSizeChange } from "../../components/RightPanel/HitAreaModal/hitAreaImageResizeSync";

const REBASELINE_MS = 400;

const readImageSize = (component) => ({
  width: toNumber(component?.componentStyles?.width, 0),
  height: toNumber(component?.componentStyles?.height, 0),
});

const isSafeImageSize = (size) =>
  Number.isFinite(size?.width) &&
  Number.isFinite(size?.height) &&
  size.width > 0 &&
  size.height > 0;

const shouldFreezeHitAreaGeometry = (baseline, nextSize) => {
  if (!isSafeImageSize(nextSize)) return true;
  if (nextSize.width < MIN_RECT_SIZE || nextSize.height < MIN_RECT_SIZE) {
    return true;
  }
  if (!baseline) return false;
  return wouldHitAreaScaleFallBelowMinSize(
    baseline.hitAreas,
    baseline.width,
    baseline.height,
    nextSize.width,
    nextSize.height
  );
};

const hasScalableHitAreas = (component) =>
  component?.componentType === "Image" &&
  Array.isArray(component?.componentStyles?.hitAreas) &&
  component.componentStyles.hitAreas.length > 0;

const cloneHitAreasSnapshot = (hitAreas = []) =>
  hitAreas.map((hitArea) => ({
    ...hitArea,
    keyframes: (hitArea?.keyframes || []).map((frame) => ({ ...frame })),
  }));

const hitAreasGeometryEqual = (left = [], right = []) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }

  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (error) {
    return false;
  }
};

/**
 * Whenever an Image layer's width/height changes, scale its hit areas to match.
 *
 * Uses a stable baseline (like canvas resize) so digit-by-digit typing
 * (200 → 20 → 2 → 0) always scales from the last settled size instead of
 * compounding crush. When width/height is 0 — or so small that any hit area
 * would drop below 5×5 — baseline geometry is restored and left undisturbed.
 */
export const useHitAreaScaleOnImageSizeChange = ({
  layerCompData,
  setLayerCompData,
  selectedComponent,
  setSelectedComponent,
  setAnimationObject,
}) => {
  const syncedSizeByImageIdRef = useRef({});
  const baselineByImageIdRef = useRef({});
  const rebaselineTimersRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(rebaselineTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
      rebaselineTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(layerCompData) || typeof setLayerCompData !== "function") {
      return;
    }

    const syncedSizeByImageId = syncedSizeByImageIdRef.current;
    const baselineByImageId = baselineByImageIdRef.current;
    const rebaselineTimers = rebaselineTimersRef.current;
    const activeImageIds = new Set();
    let nextLayers = null;
    let selectedNeedsUpdate = false;
    let nextSelectedComponent = selectedComponent;

    const clearRebaselineTimer = (imageId) => {
      if (rebaselineTimers[imageId]) {
        clearTimeout(rebaselineTimers[imageId]);
        delete rebaselineTimers[imageId];
      }
    };

    const scheduleRebaseline = (imageId, size, hitAreas) => {
      clearRebaselineTimer(imageId);
      rebaselineTimers[imageId] = setTimeout(() => {
        baselineByImageId[imageId] = {
          width: size.width,
          height: size.height,
          hitAreas: cloneHitAreasSnapshot(hitAreas),
        };
        delete rebaselineTimers[imageId];
      }, REBASELINE_MS);
    };

    const ensureBaseline = (layer, preferredSize) => {
      if (baselineByImageId[layer.id]) return baselineByImageId[layer.id];

      const size =
        preferredSize && isSafeImageSize(preferredSize)
          ? preferredSize
          : readImageSize(layer);

      if (!isSafeImageSize(size)) return null;
      if (!Array.isArray(layer?.componentStyles?.hitAreas)) return null;

      baselineByImageId[layer.id] = {
        width: size.width,
        height: size.height,
        hitAreas: cloneHitAreasSnapshot(layer.componentStyles.hitAreas),
      };
      return baselineByImageId[layer.id];
    };

    const applyHitAreaUpdate = (
      layer,
      index,
      nextSize,
      syncedHitAreas,
      { authoringSize, rebuildAnimation } = {}
    ) => {
      if (!syncedHitAreas) return;

      const nextAuthoringWidth = authoringSize?.width;
      const nextAuthoringHeight = authoringSize?.height;
      const currentAuthoringWidth = toNumber(
        layer?.componentStyles?.hitAreaAuthoringWidth,
        0
      );
      const currentAuthoringHeight = toNumber(
        layer?.componentStyles?.hitAreaAuthoringHeight,
        0
      );
      const authoringChanged =
        (Number.isFinite(nextAuthoringWidth) &&
          nextAuthoringWidth > 0 &&
          nextAuthoringWidth !== currentAuthoringWidth) ||
        (Number.isFinite(nextAuthoringHeight) &&
          nextAuthoringHeight > 0 &&
          nextAuthoringHeight !== currentAuthoringHeight) ||
        (authoringSize != null &&
          !!authoringSize.frozen !== !!layer?.componentStyles?.hitAreaGeometryFrozen);
      const hitAreasChanged = !hitAreasGeometryEqual(
        layer?.componentStyles?.hitAreas,
        syncedHitAreas
      );

      if (!hitAreasChanged && !authoringChanged) return;

      if (!nextLayers) {
        nextLayers = layerCompData.map((entry) => entry);
      }

      const nextStyles = {
        ...layer.componentStyles,
        hitAreas: syncedHitAreas,
      };
      if (Number.isFinite(nextAuthoringWidth) && nextAuthoringWidth > 0) {
        nextStyles.hitAreaAuthoringWidth = nextAuthoringWidth;
      }
      if (Number.isFinite(nextAuthoringHeight) && nextAuthoringHeight > 0) {
        nextStyles.hitAreaAuthoringHeight = nextAuthoringHeight;
      }
      if (authoringSize?.frozen) {
        nextStyles.hitAreaGeometryFrozen = true;
      } else if (authoringSize) {
        nextStyles.hitAreaGeometryFrozen = false;
      }

      nextLayers[index] = {
        ...layer,
        componentStyles: nextStyles,
      };

      if (selectedComponent?.id === layer.id) {
        selectedNeedsUpdate = true;
        nextSelectedComponent = {
          ...selectedComponent,
          componentStyles: {
            ...selectedComponent.componentStyles,
            ...nextStyles,
            // Prefer the layer's live size (may be 0 while typing).
            width: layer?.componentStyles?.width,
            height: layer?.componentStyles?.height,
          },
        };
      }

      if (rebuildAnimation && typeof setAnimationObject === "function") {
        setAnimationObject((previousAnimationObject) =>
          rebuildHitAreaAnimationEntries(previousAnimationObject, syncedHitAreas)
        );
      }
    };

    const freezeToBaseline = (layer, index, nextSize, previousSize) => {
      clearRebaselineTimer(layer.id);
      const baseline = ensureBaseline(layer, previousSize);
      syncedSizeByImageId[layer.id] = nextSize;

      if (baseline) {
        applyHitAreaUpdate(layer, index, nextSize, baseline.hitAreas, {
          authoringSize: {
            width: baseline.width,
            height: baseline.height,
            frozen: true,
          },
          rebuildAnimation: true,
        });
      }
    };

    layerCompData.forEach((layer, index) => {
      if (!layer?.id) return;

      if (!hasScalableHitAreas(layer)) {
        if (layer.componentType === "Image") {
          const size = readImageSize(layer);
          if (isSafeImageSize(size)) {
            syncedSizeByImageId[layer.id] = size;
          }
        }
        clearRebaselineTimer(layer.id);
        delete baselineByImageId[layer.id];
        return;
      }

      activeImageIds.add(layer.id);
      const nextSize = readImageSize(layer);
      const previousSize = syncedSizeByImageId[layer.id];

      // First sighting — capture baseline before any freeze/scale decision.
      if (!baselineByImageId[layer.id] && isSafeImageSize(nextSize)) {
        ensureBaseline(layer, nextSize);
        syncedSizeByImageId[layer.id] = nextSize;
        return;
      }

      const baseline = baselineByImageId[layer.id];

      // 0 size, image < 5px, or shrink that would push hit areas below 5×5.
      if (shouldFreezeHitAreaGeometry(baseline, nextSize)) {
        freezeToBaseline(layer, index, nextSize, previousSize);
        return;
      }

      if (!baseline) {
        ensureBaseline(layer, nextSize);
        syncedSizeByImageId[layer.id] = nextSize;
        return;
      }

      const sizeUnchanged =
        previousSize &&
        previousSize.width === nextSize.width &&
        previousSize.height === nextSize.height;

      if (sizeUnchanged) {
        return;
      }

      // Always scale from the settled baseline (not the previous keystroke).
      const syncedHitAreas = scaleHitAreasForImageSizeChange({
        component: {
          ...layer,
          componentStyles: {
            ...layer.componentStyles,
            hitAreas: baseline.hitAreas,
          },
        },
        previousWidth: baseline.width,
        previousHeight: baseline.height,
        nextWidth: nextSize.width,
        nextHeight: nextSize.height,
        setAnimationObject,
      });

      syncedSizeByImageId[layer.id] = nextSize;

      if (syncedHitAreas) {
        applyHitAreaUpdate(layer, index, nextSize, syncedHitAreas, {
          authoringSize: { ...nextSize, frozen: false },
        });
        scheduleRebaseline(layer.id, nextSize, syncedHitAreas);
      } else if (
        baseline.width === nextSize.width &&
        baseline.height === nextSize.height
      ) {
        // Returned to the exact baseline size (e.g. after freeze) — restore.
        applyHitAreaUpdate(layer, index, nextSize, baseline.hitAreas, {
          authoringSize: { ...nextSize, frozen: false },
          rebuildAnimation: true,
        });
      } else {
        // Scale was skipped (safety) — keep baseline geometry frozen.
        freezeToBaseline(layer, index, nextSize, previousSize);
      }
    });

    Object.keys(syncedSizeByImageId).forEach((imageId) => {
      if (!activeImageIds.has(imageId)) {
        clearRebaselineTimer(imageId);
        delete syncedSizeByImageId[imageId];
        delete baselineByImageId[imageId];
      }
    });

    // selectedComponent can briefly lead layerCompData while typing.
    if (
      hasScalableHitAreas(selectedComponent) &&
      selectedComponent?.id &&
      !selectedNeedsUpdate
    ) {
      const selectedSize = readImageSize(selectedComponent);
      const selectedId = selectedComponent.id;
      const previousSelectedSize = syncedSizeByImageId[selectedId];

      if (!baselineByImageId[selectedId] && isSafeImageSize(selectedSize)) {
        ensureBaseline(selectedComponent, selectedSize);
        syncedSizeByImageId[selectedId] = selectedSize;
      } else if (
        shouldFreezeHitAreaGeometry(baselineByImageId[selectedId], selectedSize)
      ) {
        clearRebaselineTimer(selectedId);
        const baseline = ensureBaseline(selectedComponent, previousSelectedSize);
        syncedSizeByImageId[selectedId] = selectedSize;

        if (baseline) {
          const authoringChanged =
            toNumber(selectedComponent.componentStyles?.hitAreaAuthoringWidth, 0) !==
              baseline.width ||
            toNumber(selectedComponent.componentStyles?.hitAreaAuthoringHeight, 0) !==
              baseline.height ||
            !selectedComponent.componentStyles?.hitAreaGeometryFrozen;
          const hitAreasChanged = !hitAreasGeometryEqual(
            selectedComponent.componentStyles?.hitAreas,
            baseline.hitAreas
          );

          if (hitAreasChanged || authoringChanged) {
            selectedNeedsUpdate = true;
            nextSelectedComponent = {
              ...selectedComponent,
              componentStyles: {
                ...selectedComponent.componentStyles,
                hitAreas: baseline.hitAreas,
                hitAreaAuthoringWidth: baseline.width,
                hitAreaAuthoringHeight: baseline.height,
                hitAreaGeometryFrozen: true,
              },
            };

            if (!nextLayers) {
              nextLayers = layerCompData.map((entry) => entry);
            }
            const selectedIndex = nextLayers.findIndex(
              (entry) => entry.id === selectedId
            );
            if (selectedIndex >= 0) {
              nextLayers[selectedIndex] = {
                ...nextLayers[selectedIndex],
                componentStyles: {
                  ...nextLayers[selectedIndex].componentStyles,
                  hitAreas: baseline.hitAreas,
                  hitAreaAuthoringWidth: baseline.width,
                  hitAreaAuthoringHeight: baseline.height,
                  hitAreaGeometryFrozen: true,
                },
              };
            }

            if (typeof setAnimationObject === "function") {
              setAnimationObject((previousAnimationObject) =>
                rebuildHitAreaAnimationEntries(
                  previousAnimationObject,
                  baseline.hitAreas
                )
              );
            }
          }
        }
      } else if (
        previousSelectedSize &&
        isSafeImageSize(previousSelectedSize) &&
        (previousSelectedSize.width !== selectedSize.width ||
          previousSelectedSize.height !== selectedSize.height)
      ) {
        const baseline = baselineByImageId[selectedId];
        const syncedHitAreas = scaleHitAreasForImageSizeChange({
          component: {
            ...selectedComponent,
            componentStyles: {
              ...selectedComponent.componentStyles,
              hitAreas: baseline.hitAreas,
            },
          },
          previousWidth: baseline.width,
          previousHeight: baseline.height,
          nextWidth: selectedSize.width,
          nextHeight: selectedSize.height,
          setAnimationObject,
        });

        syncedSizeByImageId[selectedId] = selectedSize;

        if (syncedHitAreas) {
          selectedNeedsUpdate = true;
          nextSelectedComponent = {
            ...selectedComponent,
            componentStyles: {
              ...selectedComponent.componentStyles,
              hitAreas: syncedHitAreas,
              hitAreaAuthoringWidth: selectedSize.width,
              hitAreaAuthoringHeight: selectedSize.height,
              hitAreaGeometryFrozen: false,
            },
          };

          if (!nextLayers) {
            nextLayers = layerCompData.map((entry) => entry);
          }
          const selectedIndex = nextLayers.findIndex(
            (entry) => entry.id === selectedId
          );
          if (selectedIndex >= 0) {
            nextLayers[selectedIndex] = {
              ...nextLayers[selectedIndex],
              componentStyles: {
                ...nextLayers[selectedIndex].componentStyles,
                hitAreas: syncedHitAreas,
                hitAreaAuthoringWidth: selectedSize.width,
                hitAreaAuthoringHeight: selectedSize.height,
                hitAreaGeometryFrozen: false,
              },
            };
          }

          scheduleRebaseline(selectedId, selectedSize, syncedHitAreas);
        } else if (baseline) {
          // Scale skipped — keep baseline geometry.
          const hitAreasChanged = !hitAreasGeometryEqual(
            selectedComponent.componentStyles?.hitAreas,
            baseline.hitAreas
          );
          if (hitAreasChanged) {
            selectedNeedsUpdate = true;
            nextSelectedComponent = {
              ...selectedComponent,
              componentStyles: {
                ...selectedComponent.componentStyles,
                hitAreas: baseline.hitAreas,
                hitAreaAuthoringWidth: baseline.width,
                hitAreaAuthoringHeight: baseline.height,
                hitAreaGeometryFrozen: true,
              },
            };
          }
        }
      } else if (!previousSelectedSize || !isSafeImageSize(previousSelectedSize)) {
        syncedSizeByImageId[selectedId] = selectedSize;
      }
    }

    if (nextLayers) {
      setLayerCompData(nextLayers);
    }
    if (selectedNeedsUpdate && typeof setSelectedComponent === "function") {
      setSelectedComponent(nextSelectedComponent);
    }
  }, [
    layerCompData,
    selectedComponent,
    setAnimationObject,
    setLayerCompData,
    setSelectedComponent,
  ]);
};
